import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { FieldPath, UseFormReturn } from "react-hook-form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { DateTime } from "luxon";
import { Box, Dialog, DialogContent, styled } from "@mui/material";

import { Passenger } from "@/data/models/passenger.model";
import { TripLegType, type ReservationValidations } from "@/data/models/reservation.model";
import { ServicePlan } from "@/data/models/service-plan.model";
import {
  ReservationFormInput,
  reservationFormSchema,
  emptyReservationFormValues,
  type ReservationFormValues,
} from "./schema/reservationForm.schema";

import reservationUtils from "@/services/reservationUtils";

import {
  ReservationFormMachinesProvider,
  useReservationFormMachines,
} from "./ReservationFormMachinesContext";

import ReservationDialogHeader from "@/components/home/reservations/ReservationDialogHeader";
import { ReservationTripLegForm } from "./ReservationTripLegForm";
import { ReservationMemberForm } from "./ReservationMemberForm";
import { ReservationSummary } from "./ReservationSummary";
import OverrideDialog from "../home/reservations/OverrideDialog";
import ConfirmationDialog from "../ConfirmationDialog";

const PREFIX = 'CreateReservationDialog';

const classes = {
  content: `${PREFIX}-content`,
  pages: `${PREFIX}-pages`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.content}`]: {
    padding: 0,
    overflow: 'visible',
  },
  [`& .${classes.pages}`]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(3),
    maxWidth: '1152px',
    margin: '0 auto',
    [theme.breakpoints.up('md')]: {
      paddingLeft: theme.spacing(10),
      paddingRight: theme.spacing(10),
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(3),
    },
    [theme.breakpoints.up('lg')]: {
      paddingLeft: theme.spacing(18),
      paddingRight: theme.spacing(18),
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(3),
    },
  },
}));

function ReservationFormSubmit({
  form,
  onSubmit,
}: {
  form: UseFormReturn<ReservationFormInput, unknown, ReservationFormValues>;
  onSubmit: (
    values: ReservationFormValues,
    options: { saveAsDraft: boolean },
  ) => Promise<void>;
}) {
  const { mainSnapshot } = useReservationFormMachines();

  const wasSuccessRef = useRef(false);
  useEffect(() => {
    if (!mainSnapshot.matches('success')) {
      wasSuccessRef.current = false;
      return;
    }
    if (wasSuccessRef.current) return;
    wasSuccessRef.current = true;
    if (onSubmit) {
      onSubmit(form.getValues() as ReservationFormValues, {
        saveAsDraft: Boolean(mainSnapshot.context.saveAsDraft),
      });
    }
  }, [mainSnapshot, onSubmit, form]);

  return null;
}

interface CreateReservationDialogProps {
  open: boolean;
  onClose: () => void;
  passenger: Passenger | null;
  servicePlans: ServicePlan[];
}

const CreateReservationDialog = ({
  open,
  onClose,
  passenger,
  servicePlans,
}: CreateReservationDialogProps) => {
  const { t } = useTranslation('common');
  const [activeStep, setActiveStep] = useState(0);
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [isOverridden, setIsOverridden] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const form = useForm<ReservationFormInput, unknown, ReservationFormValues>({
    resolver: zodResolver(reservationFormSchema),
    defaultValues: emptyReservationFormValues,
    mode: 'onSubmit',
  });

  const applyTripLegValidationRef = useRef<
    (validations: ReservationValidations) => void
  >(() => {});

  /**
   * Validation endpoint results are applied to the form here.
   */
  useEffect(() => {
    applyTripLegValidationRef.current = (
      validations: ReservationValidations,
    ) => {
      // Update the form values returned from validation results. Apparently only timezones
      for (const update of validations.tripLegUpdates) {
        const idx = update.tripLegSequence - 1;
        if (update.pickUpAddress?.timeZone) {
          if (form.getValues('trips')[idx] == null) continue;
          if (
            form.getValues('trips')[idx].pickupTimeZone !==
            update.pickUpAddress.timeZone
          ) {
            form.setValue(
              `trips.${idx}.pickupTimeZone`,
              update.pickUpAddress.timeZone,
              { shouldValidate: true },
            );
          }
        }
        if (update.dropOffAddress?.timeZone) {
          if (form.getValues('trips')[idx] == null) continue;
          if (
            form.getValues('trips')[idx].dropoffTimeZone !==
            update.dropOffAddress.timeZone
          ) {
            form.setValue(
              `trips.${idx}.dropoffTimeZone`,
              update.dropOffAddress.timeZone,
              { shouldValidate: true },
            );
          }
        }
      }
      // Set field specific errors from validation results
      form.clearErrors(['trips', 'genericErrors']);
      if (validations.errors.length > 0) {
        for (const error of validations.errors) {
          const idx = error.tripLegSequence ? error.tripLegSequence - 1 : 0;
          // We may have round trip errors and no round trip form values, so just pass
          if (form.getValues('trips')[idx] == null) continue;

          const tripType = form.getValues('tripType');
          const messages = reservationUtils.getReservationFormFieldErrors(
            t,
            tripType === TripLegType.RoundTrip,
            validations,
          );

          // For each message that isn't a generic error, set the error on the corresponding field
          const genericErrors: string[] = [];
          for (const message of messages) {
            if (message.field !== 'GenericError') {
              const fieldPath =
                message.field as keyof ReservationFormInput['trips'][number][keyof ReservationFormInput['trips'][number]];
              if (form.getValues(fieldPath) == null) continue;
              form.setError(fieldPath, {
                type: 'manual',
                message: message.message,
              });
            } else {
              genericErrors.push(message.message);
            }
          }

          // For all generic messages, set the error on the GenericErrors field
          if (genericErrors.length > 0) {
            form.setError('genericErrors', {
              type: 'manual',
              message: Array.from(new Set(genericErrors)).join('\n'),
            });
          }
        }
      } else {
        form.clearErrors();
      }
    };
  }, [form]);

  const pickUpTime = form.watch('trips.0.pickupDateTime');
  const appointmentDate = form.watch('trips.0.appointmentDate');
  const tripLegServiceDate =
    (pickUpTime instanceof DateTime && pickUpTime.isValid
      ? pickUpTime.toISODate()
      : null) ??
    (appointmentDate instanceof DateTime && appointmentDate.isValid
      ? appointmentDate.toISODate()
      : null) ??
    (typeof appointmentDate === 'string' && appointmentDate.trim()
      ? appointmentDate
      : null) ??
    undefined;
  const overridableServicePlans = servicePlans.filter(
    ({ effectiveStartDate, effectiveEndDate }) =>
      tripLegServiceDate != null &&
      effectiveStartDate <= tripLegServiceDate &&
      (!effectiveEndDate || tripLegServiceDate <= effectiveEndDate),
  );

  const handleStep = (step: number) => () => {
    setActiveStep(step);
  };

  const handleReservationSubmit = useCallback(
    async (
      _values: ReservationFormValues,
      options: { saveAsDraft: boolean },
    ) => {
      if (options.saveAsDraft) {
        form.reset(emptyReservationFormValues);
        setActiveStep(0);
        onClose();
        return;
      }
      setActiveStep((prev) => prev + 1);
    },
    [form, onClose],
  );

  const handlePrevious = (evt: any) => {
    evt.preventDefault();
    setActiveStep(activeStep - 1);
  };

  const handleNext = (evt: any) => {
    evt.preventDefault();
    setActiveStep(activeStep + 1);
  };

  const handleClose = () => {
    onClose();
  };

  const handleOverrideOpen = () => {
    setOverrideDialogOpen(true);
  };

  const handleOverrideClose = (data: any | null) => {
    setOverrideDialogOpen(false);
  };

  const handleConfirmationDialogClose = () => {
    setConfirmDialogOpen(false);
  };

  return (
    <StyledDialog open={open} onClose={onClose} fullScreen>
      <ReservationDialogHeader
        passengerName={
          passenger ? `${passenger.firstName} ${passenger.lastName}` : ''
        }
        title={t('reservation.createTrip')}
        steps={[
          t('reservation.tripDetailsStep'),
          t('reservation.member'),
          t('reservation.summary'),
        ]}
        activeStep={activeStep}
        isValid={true}
        step={handleStep}
        open={onClose}
      />
      <DialogContent className={classes.content}>
        <Box className={classes.pages}>
          <ReservationFormMachinesProvider
            clientBC={passenger?.clientBC ?? ''}
            passengerUUID={passenger?.passengerUUID ?? ''}
            passengerPhone={
              passenger
                ? reservationUtils.getPassengerPhone(passenger)
                : undefined
            }
            applyTripLegValidationResultsToForm={() =>
              applyTripLegValidationRef.current
            }>
            <ReservationFormSubmit
              form={form}
              onSubmit={handleReservationSubmit}
            />
            {activeStep === 0 && (
              <ReservationTripLegForm
                form={form}
                onNext={handleNext}
                servicePlans={servicePlans}
              />
            )}
            {activeStep === 1 && (
              <ReservationMemberForm
                form={form}
                onNext={handleNext}
                onPrevious={handlePrevious}
              />
            )}
            {activeStep === 2 && (
              <ReservationSummary form={form} onPrevious={handlePrevious} />
            )}
          </ReservationFormMachinesProvider>
        </Box>
      </DialogContent>
      <OverrideDialog
        open={overrideDialogOpen}
        servicePlans={overridableServicePlans}
        close={handleOverrideClose}
      />
      <ConfirmationDialog
        title={t('common.discardChanges')}
        message={t('common.possibleDataLoss')}
        open={confirmDialogOpen}
        positiveAction={handleClose}
        negativeAction={handleConfirmationDialogClose}
      />
    </StyledDialog>
  );
};

export { CreateReservationDialog };
